import { useCallback, useEffect, useState } from "react";

// Day/Night UI design tokens consumed by the HUD (TopBar, AreaNav, InfoPanel, pages).
// The brand yellow (#FFCE24) stays constant across both themes.
export function getUITheme(night) {
  if (night) {
    return {
      night: true,
      pageBg: "#0E121C",
      headerBg: "#10141E",
      headerBorder: "rgba(255,255,255,0.08)",
      panelBg: "#161B27",
      panelBorder: "rgba(255,255,255,0.09)",
      panelSurface: "#1E2433",
      infoBg: "#141926",
      textStrong: "#ECEEF4",
      textBody: "#AAB2C4",
      textMuted: "#79839A",
      itemActiveBg: "#FFCE24",
      itemActiveText: "#10141E",
      itemIdleText: "#C7CDDB",
      hoverClass: "hover:bg-white/5",
      chipBg: "rgba(22,27,39,0.92)",
      chipText: "#ECEEF4",
      divider: "rgba(255,255,255,0.08)",
      categoryPillBg: "rgba(255,255,255,0.08)",
      categoryPillText: "#ECEEF4",
      selectorBg: "rgba(20,25,38,0.94)",
    };
  }
  return {
    night: false,
    pageBg: "#EFEEE9",
    headerBg: "#2B2E34",
    headerBorder: "rgba(255,255,255,0.1)",
    panelBg: "#F6F6F6",
    panelBorder: "rgba(0,0,0,0.1)",
    panelSurface: "#FFFFFF",
    infoBg: "#FFFFFF",
    textStrong: "#2B2E34",
    textBody: "#404040",
    textMuted: "#848484",
    itemActiveBg: "#2B2E34",
    itemActiveText: "#FFFFFF",
    itemIdleText: "#2B2E34",
    hoverClass: "hover:bg-black/5",
    chipBg: "rgba(255,255,255,0.9)",
    chipText: "#2B2E34",
    divider: "rgba(0,0,0,0.08)",
    categoryPillBg: "#2B2E34",
    categoryPillText: "#FFFFFF",
    selectorBg: "rgba(255,255,255,0.96)",
  };
}

// Pick a readable foreground color for a given background hex (charcoal or white).
export function contrastColor(hex) {
  if (!hex || typeof hex !== "string") return "#2B2E34";
  const c = hex.replace("#", "");
  const full = c.length === 3 ? c.split("").map((x) => x + x).join("") : c;
  if (full.length < 6) return "#2B2E34";
  const r = parseInt(full.slice(0, 2), 16);
  const g = parseInt(full.slice(2, 4), 16);
  const b = parseInt(full.slice(4, 6), 16);
  const lum = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return lum > 0.62 ? "#2B2E34" : "#FFFFFF";
}

// Day/Night state hook with optional localStorage persistence.
export function useDayNight(defaultNight = false, persistKey = "sm-iso-night") {
  const [night, setNight] = useState(() => {
    if (typeof window === "undefined" || !persistKey) return defaultNight;
    try {
      const saved = window.localStorage.getItem(persistKey);
      if (saved === "night") return true;
      if (saved === "day") return false;
    } catch (e) {
      /* ignore */
    }
    return defaultNight;
  });

  useEffect(() => {
    if (typeof window === "undefined" || !persistKey) return;
    try {
      window.localStorage.setItem(persistKey, night ? "night" : "day");
    } catch (e) {
      /* ignore */
    }
  }, [night, persistKey]);

  const toggle = useCallback(() => setNight((n) => !n), []);
  return { night, setNight, toggle };
}
